import availableFonts from "@builder/fonts/availableFonts";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import "./onboard.scss";
import { palettes } from "./starterTemplates/colorPalettes";
import { layoutStyles } from "./starterTemplates/layoutStyles";
import { POSOnboardSetup, useApplyOnboardSetup } from "./useApplyOnboardSetup";
import Button from "@builder/components/general/Button";
import OnboardActivityTexts from "@builder/components/onboard/components/rightSidebar/OnboardActivityTexts";
import { BuilderSliceActions } from "@builder/store/slices/BuilderSlice/builderSlice";
import { setScaleMode, setZoomDisabled } from "@builder/store/slices/frameTransformSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { setCurrentPopup } from "@builder/store/slices/popupSlice";
import OliverText from "../general/OliverText";
import { ProgressBar } from "./components/progressbar/ProgressBar";
import {
    SHORT_ACTIVITIES_CREATING_POS,
    useGenerateActivitiesWithMinimalInterval as useStreamActivitiesWithMinimalInterval
} from "./components/rightSidebar/temporary_fake_activities";
import { OnboardStepsIndex } from "./steps";
import useWindowSize from "@builder/hooks/useWindowSize";

export interface ValidationResult {
    valid: boolean;
    error?: string;
}

const BACKDROP_FILTER_FACTOR = 4;

const Onboard = () => {
    const dispatch = useAppDispatch();
    const [internalOpen, setInternalOpen] = useState<boolean>(false);
    const { company } = useAppSelector(state => state.storeData);
    const [posOnboardSetup, setPOSOnboardSetup] = useState<POSOnboardSetup>({
        currentStepIdx: 0,
        colors: { selectedColorPaletteId: palettes[0].id, globalColors: palettes[0].colors },
        selectedLayoutId: layoutStyles[0].id,
        selectedFontName: availableFonts[0].name
    });
    const params = new URLSearchParams(location.search);
    const onboard = params.has("onboard");
    const [isClosing, setIsClosing] = useState<boolean>(false);

    // todo: I should have probably used context instead of weird prop drilling. todo, it's not too bad.
    const validateCurrentStepRef = useRef<() => ValidationResult>(() => ({ valid: true }));
    const { isMobile } = useWindowSize();
    const registerValidator = (fn: () => ValidationResult) => {
        validateCurrentStepRef.current = fn;
    };

    useEffect(() => {
        dispatch(setScaleMode("fit"));
    }, []);

    useEffect(() => {
        validateCurrentStepRef.current = () => ({ valid: true });
    }, [posOnboardSetup.currentStepIdx]);

    useEffect(() => {
        if (onboard) {
            dispatch(setScaleMode("fit"));
            dispatch(setZoomDisabled(true));
            setInternalOpen(true);
        }
    }, [onboard, params, setInternalOpen]);

    const isLastStep = posOnboardSetup.currentStepIdx === OnboardStepsIndex.length - 1;
    const { isDone, isFinalScreen, setIsFinalScreen } = useApplyOnboardSetup({ setup: posOnboardSetup, setSetup: setPOSOnboardSetup, enabled: internalOpen, isMobile });
    const currentActions = useStreamActivitiesWithMinimalInterval({
        isDone: isDone,
        activityArray: isMobile && !isFinalScreen ? [SHORT_ACTIVITIES_CREATING_POS[0]] : SHORT_ACTIVITIES_CREATING_POS,
        options: { stopWhenReachedEnd: false }
    });

    // Auto-open onboardComplete popup when onboarding is complete
    useEffect(() => {
        if (isDone && company?.registrationCompleted) {
            dispatch(setCurrentPopup("onboardComplete"));
        }
    }, [isDone, company?.registrationCompleted, dispatch]);

    if (!internalOpen || !onboard) return null;

    const closeOnboard = () => {
        setIsClosing(true);
        setTimeout(() => {
            setInternalOpen(false);
            dispatch(BuilderSliceActions.updateBuildHistory({ past: [], future: [] }));
            dispatch(setZoomDisabled(false));
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete("onboard");
            window.history.replaceState({}, "", newUrl.toString());

        }, 1000);
    };

    const handleNext = async () => {
        const { valid, error } = validateCurrentStepRef.current();
        if (!valid) {
            toast.error(error || "Please complete all required fields");
            console.error("Onboard validation failed:", error, posOnboardSetup);
            return;
        }
        if (isLastStep) {
            setIsFinalScreen(true);
            return;
        } else {
            setPOSOnboardSetup(prev => ({ ...prev, currentStepIdx: prev.currentStepIdx + 1 }));
        }
    };

    const handleBack = () => {
        if (posOnboardSetup.currentStepIdx > 0) setPOSOnboardSetup(prev => ({ ...prev, currentStepIdx: prev.currentStepIdx - 1 }));
    };

    return (
        <div
            className={`onboard-backdrop ${isClosing ? "closing" : ""}`}
            style={{ backdropFilter: `blur(${(OnboardStepsIndex.length / (posOnboardSetup.currentStepIdx + 1)) * BACKDROP_FILTER_FACTOR + 5}px)` }}
        >
            <div className={`onboard-popup ${isFinalScreen ? "final-screen" : ""}`} onClick={e => e.stopPropagation()}>
                <div className="onboard-left">
                    <div className="scrollable-container">
                        <nav className="onboard-top-nav">
                            <div className="onboard-logo-container">
                                <img src="/final-logo-full.svg" alt="final-logo-full" className="onboard-logo" />
                            </div>
                            <div className="details-and-progress">
                                <OliverText
                                    text={OnboardStepsIndex[posOnboardSetup.currentStepIdx].shortDescription}
                                    size="m"
                                    weight="medium-heavy"
                                    color="base-grey"
                                />
                                <ProgressBar currentStepIdx={posOnboardSetup.currentStepIdx} totalSteps={OnboardStepsIndex.length} />
                            </div>
                        </nav>

                        <div className="step-component-container">
                            {(() => {
                                const Component = OnboardStepsIndex[posOnboardSetup.currentStepIdx].component;
                                return (
                                    <Component
                                        posOnboardSetup={posOnboardSetup}
                                        setPOSOnboardSetup={setPOSOnboardSetup}
                                        setValidateCurrentStep={registerValidator}
                                    />
                                );
                            })()}
                        </div>
                    </div>
                    <nav className="onboard-bottom-nav">
                        {posOnboardSetup.currentStepIdx > 0 && (
                            <Button className="btn-back" buttonStyle="secondary" size="small" onClick={handleBack} text="Back" />
                        )}
                        <Button
                            buttonStyle="primary"
                            size="small"
                            className="next-btn"
                            onClick={handleNext}
                            text={isLastStep ? "Let's see the result" : "Next"}
                        />
                    </nav>
                </div>

                <div className="onboard-right">
                    <OnboardActivityTexts currentActions={currentActions} isDone={isDone} closeOnboard={closeOnboard} />
                </div>
            </div>
        </div>
    );
};

export default Onboard;
